/**
 * Optimistic Update Manager
 * 
 * Manages optimistic UI updates with server reconciliation and rollback.
 * Ensures UI responsiveness while maintaining data consistency.
 */

export interface OptimisticUpdate {
  id: string;
  key: string;
  type: 'player' | 'inventory' | 'equipment' | 'stats' | 'custom';
  optimisticData: any;
  originalData: any;
  timestamp: number;
  status: 'pending' | 'confirmed' | 'failed';
  serverPromise: Promise<any>;
  onSuccess?: (data: any) => void;
  onFailure?: (error: any) => void;
}

export interface OptimisticStats {
  totalUpdates: number;
  confirmedUpdates: number;
  failedUpdates: number;
  pendingUpdates: number;
  averageLatency: number;
}

export class OptimisticUpdateManager {
  private static updates: Map<string, OptimisticUpdate> = new Map();
  private static stats: OptimisticStats = {
    totalUpdates: 0,
    confirmedUpdates: 0,
    failedUpdates: 0,
    pendingUpdates: 0,
    averageLatency: 0,
  };
  private static latencies: number[] = [];

  /**
   * Create an optimistic update
   */
  static createUpdate(
    key: string,
    type: OptimisticUpdate['type'],
    optimisticData: any,
    originalData: any,
    serverPromise: Promise<any>,
    callbacks?: { onSuccess?: (data: any) => void; onFailure?: (error: any) => void }
  ): string {
    const id = `${key}_${Date.now()}_${Math.random()}`;
    const startTime = Date.now();

    const update: OptimisticUpdate = {
      id,
      key,
      type,
      optimisticData,
      originalData,
      timestamp: startTime,
      status: 'pending',
      serverPromise,
      onSuccess: callbacks?.onSuccess,
      onFailure: callbacks?.onFailure,
    };

    this.updates.set(id, update);
    this.stats.totalUpdates++;
    this.stats.pendingUpdates++;

    console.log(`✨ Optimistic update created: ${key} (${type})`);

    // Handle server response
    serverPromise
      .then((data) => {
        this.confirmUpdate(id, data, Date.now() - startTime);
      })
      .catch((error) => {
        this.failUpdate(id, error);
      });

    return id;
  }

  /**
   * Confirm an optimistic update with server data
   */
  private static confirmUpdate(id: string, serverData: any, latency: number): void {
    const update = this.updates.get(id);
    if (!update) return;

    update.status = 'confirmed';
    this.stats.pendingUpdates--;
    this.stats.confirmedUpdates++;

    // Track latency
    this.latencies.push(latency);
    if (this.latencies.length > 100) {
      this.latencies.shift();
    }
    this.stats.averageLatency =
      this.latencies.reduce((a, b) => a + b, 0) / this.latencies.length;

    console.log(`✅ Optimistic update confirmed: ${update.key} (${latency}ms)`);

    update.onSuccess?.(serverData);
  }

  /**
   * Fail an optimistic update and rollback
   */
  private static failUpdate(id: string, error: any): void {
    const update = this.updates.get(id);
    if (!update) return;

    update.status = 'failed';
    this.stats.pendingUpdates--;
    this.stats.failedUpdates++;

    console.error(`❌ Optimistic update failed: ${update.key}`, error);

    // Rollback to original data
    console.log(`🔄 Rolling back to original data for ${update.key}`);

    update.onFailure?.(error);
  }

  /**
   * Get update status
   */
  static getUpdateStatus(id: string): OptimisticUpdate['status'] | null {
    return this.updates.get(id)?.status || null;
  }

  /**
   * Get pending updates
   */
  static getPendingUpdates(): OptimisticUpdate[] {
    return Array.from(this.updates.values()).filter((u) => u.status === 'pending');
  }

  /**
   * Get failed updates
   */
  static getFailedUpdates(): OptimisticUpdate[] {
    return Array.from(this.updates.values()).filter((u) => u.status === 'failed');
  }

  /**
   * Wait for update confirmation
   */
  static async waitForConfirmation(id: string, timeout: number = 30000): Promise<boolean> {
    const startTime = Date.now();

    while (Date.now() - startTime < timeout) {
      const update = this.updates.get(id);
      if (!update) return false;

      if (update.status === 'confirmed') {
        return true;
      } else if (update.status === 'failed') {
        return false;
      }

      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    return false;
  }

  /**
   * Get statistics
   */
  static getStats(): OptimisticStats {
    return { ...this.stats };
  }

  /**
   * Clear old updates
   */
  static clearOldUpdates(olderThanMs: number = 300000): void {
    const cutoff = Date.now() - olderThanMs;
    let cleared = 0;

    Array.from(this.updates.entries()).forEach(([id, update]) => {
      if (update.timestamp < cutoff && update.status !== 'pending') {
        this.updates.delete(id);
        cleared++;
      }
    });

    if (cleared > 0) {
      console.log(`🧹 Cleared ${cleared} old optimistic updates`);
    }
  }

  /**
   * Clear all updates
   */
  static clearAll(): void {
    this.updates.clear();
    this.stats = {
      totalUpdates: 0,
      confirmedUpdates: 0,
      failedUpdates: 0,
      pendingUpdates: 0,
      averageLatency: 0,
    };
    this.latencies = [];
    console.log('🧹 Cleared all optimistic updates');
  }
}

export default OptimisticUpdateManager;
